// LEVEL-UP MOMENT (owner 2026-11-02: "need some better effects for leveling up"; "maybe when you level up, the character
// clenches his fist and starts doing, like, charging up"). Pure sequencing — no DOM, no three — so the rules are unit-tested
// (tests/levelUpSeq.test.ts). main.ts asks it what to play when a draft opens; the view (src/render/levelUpFx.ts) and the HUD
// (hud.showLevelUp reveal classes) play it. Presentation only: the sim is already paused in 'levelup' and never waits on it;
// the intro only delays when the cards are SHOWN (render time), and any input skips straight to them.
//
//  - a real level-up (one or more `levelup` SimEvents since the last draft) plays the in-world burst ONCE, with ×N when
//    several drafts are queued; chained drafts of the same batch get a quick card reveal and no burst;
//  - a free pick (relic cache / chest fusion offer) has no burst (it is not a level), just the card reveal;
//  - returning to a draft from the pause menu shows it as it was (no replay);
//  - prefers-reduced-motion: no intro delay and no hero action, cards fade in; Reduce flashing: cards fade in (no glint);
//  - hero charge-up action (behind ?lupose=1 until the owner approves the preview): FULL (0.6 s) on milestone levels
//    (every 5th) or when a minute of run time has passed since the last full one, QUICK (0.32 s) otherwise.

export type PoseKind = 'full' | 'quick' | 'none';
/** card reveal: full = stamp + fly-in + glint + accents, quick = shorter fly-in (chained draft), fade = simple fade
 *  (reduced motion / Reduce flashing), none = shown as-is (back from the pause menu) */
export type RevealKind = 'full' | 'quick' | 'fade' | 'none';
export type ConfirmFx = 'evolve' | 'fusion' | null;

export interface LuOpenIn {
  /** sim.pendingLevels: drafts waiting, this one included */
  pending: number;
  /** the hero's level now */
  level: number;
  /** run time (s) — for the once-a-minute full action */
  runTime: number;
  /** the draft is coming back from the pause menu (or settings opened from it) */
  resumed: boolean;
  /** prefers-reduced-motion */
  reducedMotion: boolean;
  /** Reduce flashing is on (any level) */
  calm: boolean;
  /** hero charge-up action allowed (?lupose=1 until approved) */
  pose: boolean;
}

export interface LuPlan {
  /** play the in-world burst on the hero */
  burst: boolean;
  /** ×N badge (1 = no badge) */
  count: number;
  pose: PoseKind;
  /** seconds before the cards are shown (0 = at once) */
  intro: number;
  /** seconds into the intro at which the burst erupts (the hero's release beat; 0 without an action) */
  burstAt: number;
  reveal: RevealKind;
}

/** intro without a hero action: the burst rises, then the cards open (s) */
export const LU_INTRO = 0.36;
/** hero charge-up action lengths (s): full on milestones / once a minute, quick otherwise */
export const LU_POSE_FULL = 0.6;
export const LU_POSE_QUICK = 0.32;
/** share of the action at which the burst erupts (the release / battle-cry key of the clip) */
export const LU_RELEASE = 0.86;
/** a full action at most this often (s of run time), plus every MILESTONE-th level */
export const LU_FULL_GAP = 60;
export const LU_MILESTONE = 5;
/** the card reveal is done by then (s) — CSS keeps every animation inside it (owner: never slow choosing) */
export const LU_REVEAL_MS = 450;

/** which hero action a level-up gets */
export function poseFor(level: number, runTime: number, lastFull: number): Exclude<PoseKind, 'none'> {
  return level % LU_MILESTONE === 0 || runTime - lastFull >= LU_FULL_GAP ? 'full' : 'quick';
}

/** a picked card that earns the stronger confirm burst as the run resumes */
export function confirmFx(kind: string | undefined): ConfirmFx {
  return kind === 'evolve' ? 'evolve' : kind === 'fusion' ? 'fusion' : null;
}

export class LevelUpSeq {
  /** `levelup` events since the last draft opened */
  private events = 0;
  private lastFull = -Infinity;
  /** seconds left before the cards show (0 = cards shown / no intro) */
  intro = 0;
  /** seconds since the intro started (for the burst's release beat) */
  introAge = 0;
  plan: LuPlan | null = null;
  private burstFired = false;
  /** QA counters */
  stats = { bursts: 0, skipped: 0, full: 0, quick: 0 };

  /** a run starts */
  reset(): void { this.events = 0; this.lastFull = -Infinity; this.intro = 0; this.introAge = 0; this.plan = null; this.burstFired = false; }

  /** a `levelup` SimEvent was processed */
  levelEvent(): void { this.events++; }

  /** a draft screen opens: what to play */
  open(i: LuOpenIn): LuPlan {
    const real = this.events > 0 && !i.resumed;
    const count = real ? Math.max(this.events, i.pending, 1) : 1;
    this.events = 0;
    let pose: PoseKind = 'none';
    if (real && i.pose && !i.reducedMotion) {
      pose = poseFor(i.level, i.runTime, this.lastFull);
      if (pose === 'full') { this.lastFull = i.runTime; this.stats.full++; } else this.stats.quick++;
    }
    const dur = pose === 'full' ? LU_POSE_FULL : pose === 'quick' ? LU_POSE_QUICK : LU_INTRO;
    const intro = !real || i.reducedMotion ? 0 : dur;
    const reveal: RevealKind = i.resumed ? 'none' : i.reducedMotion || i.calm ? 'fade' : real ? 'full' : 'quick';
    const plan: LuPlan = { burst: real, count, pose, intro, burstAt: pose === 'none' ? 0 : dur * LU_RELEASE, reveal };
    this.plan = plan; this.intro = intro; this.introAge = 0; this.burstFired = false;
    if (real) this.stats.bursts++;
    return plan;
  }

  /** the burst should erupt now (once per plan): at the release beat of the hero action, or at once without one */
  takeBurst(): boolean {
    if (!this.plan?.burst || this.burstFired) return false;
    if (this.intro > 0 && this.introAge < this.plan.burstAt) return false;
    this.burstFired = true; return true;
  }

  /** advance the intro on real time; true on the frame the cards should open */
  tick(dt: number): boolean {
    if (this.intro <= 0) return false;
    this.introAge += dt; this.intro -= dt;
    if (this.intro <= 0) { this.intro = 0; return true; }
    return false;
  }

  /** the hero action could not play (no authored model): burst at once, the plain intro */
  noPose(intro = LU_INTRO): void {
    if (!this.plan || this.plan.pose === 'none') return;
    if (this.plan.pose === 'full') this.stats.full--; else this.stats.quick--;
    this.plan.pose = 'none'; this.plan.burstAt = 0; this.plan.intro = Math.min(this.plan.intro, intro); this.intro = Math.min(this.intro, intro);
  }

  get inIntro(): boolean { return this.intro > 0; }

  /** any input during the intro: show the cards now (the burst still erupts). true when there was an intro to skip */
  skip(): boolean {
    if (this.intro <= 0) return false;
    this.intro = 0; this.introAge = this.plan?.burstAt ?? 0; this.stats.skipped++;
    return true;
  }
}
